import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Wrench, FileCheck, Upload, Loader2, MapPin, Euro, ClipboardCheck,
  AlertCircle, ShieldCheck, ArrowRight, Check,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AppHeader } from "@/components/AppHeader";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";

/**
 * Installateur-Onboarding-Wizard.
 *
 * Wird angezeigt für Installateur-Accounts mit ``status=pending_admin``.
 * 4 Schritte:
 *  1. HWK (Handwerkskammer-Eintragung)
 *  2. Versicherungsnachweis-Upload (PDF / Foto)
 *  3. Servicegebiet & Stundensatz
 *  4. Übersicht & Absenden zur Admin-Prüfung
 */
export default function InstallerOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [step, setStep] = useState(1);

  // form state mirrors profile
  const [form, setForm] = useState({
    hwk_number: "",
    hwk_authority: "",
    insurance_expires_at: "",
    plz_prefixes: "",
    radius_km: "",
    hourly_rate: "",
    references_text: "",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/installer/onboarding/me");
        if (cancelled) return;
        setProfile(data.profile);
        const sa = data.profile.service_area || {};
        setForm((f) => ({
          ...f,
          hwk_number: data.profile.hwk_number || "",
          hwk_authority: data.profile.hwk_authority || "",
          insurance_expires_at: data.profile.insurance_expires_at || "",
          plz_prefixes: (sa.plz_prefixes || []).join(", "),
          radius_km: sa.radius_km ?? "",
          hourly_rate: data.profile.hourly_rate ?? "",
          references_text: data.profile.references_text || "",
        }));
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Konnte Profil nicht laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const savePartial = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/installer/onboarding/me", patch);
      setProfile(data);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 8 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/installer/onboarding/upload-insurance", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Reload profile to reflect new doc info
      const { data } = await api.get("/installer/onboarding/me");
      setProfile(data.profile);
      toast.success("Versicherungsnachweis hochgeladen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/installer/onboarding/submit");
      toast.success("Bewerbung abgesendet — wir prüfen sie innerhalb von 2 Werktagen.");
      const { data } = await api.get("/installer/onboarding/me");
      setProfile(data.profile);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Absenden fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-400">
        <Loader2 className="animate-spin mr-2" size={16} /> lädt…
      </div>
    );
  }

  const status = profile?.status;
  if (status === "submitted") {
    return <SubmittedView profile={profile} />;
  }
  if (status === "approved") {
    return <ApprovedView onContinue={() => navigate("/dashboard")} />;
  }
  if (status === "rejected") {
    return <RejectedView profile={profile} />;
  }

  const stepDef = [
    { n: 1, title: "Handwerkskammer", icon: ClipboardCheck },
    { n: 2, title: "Versicherungsnachweis", icon: ShieldCheck },
    { n: 3, title: "Servicegebiet & Konditionen", icon: MapPin },
    { n: 4, title: "Übersicht & Absenden", icon: FileCheck },
  ];

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="installer-onboarding">
      <AppHeader title="Installateur-Bewerbung" />
      <main className="max-w-3xl mx-auto px-3 sm:px-6 py-5 space-y-5">
        {/* Welcome */}
        <div className="bg-gradient-to-br from-[#00E575]/15 to-transparent border border-[#00E575]/30 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <Wrench className="text-[#00E575]" size={24} />
            <div>
              <h1 className="font-display text-xl font-bold mb-1">Willkommen, {user?.first_name || "Installateur"}!</h1>
              <p className="text-[13px] text-zinc-300 leading-relaxed">
                Damit du auf Montageaufträge bieten kannst, brauchen wir noch ein paar Angaben.
                Die Prüfung dauert in der Regel <b>1–2 Werktage</b>.
              </p>
            </div>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between bg-[#18181B] border border-zinc-800 rounded-xl p-3">
          {stepDef.map((s, i) => {
            const done = step > s.n;
            const active = step === s.n;
            const Icon = s.icon;
            return (
              <button
                key={s.n}
                onClick={() => setStep(s.n)}
                data-testid={`onboarding-step-${s.n}`}
                className={`flex-1 flex flex-col items-center gap-1 px-1 ${i > 0 ? "border-l border-zinc-800" : ""} ${done || active ? "text-zinc-100" : "text-zinc-500"}`}
              >
                <div className={`h-8 w-8 rounded-full flex items-center justify-center border ${active ? "bg-[#00E575] text-[#09090B] border-[#00E575]" : done ? "bg-[#00E575]/20 text-[#00E575] border-[#00E575]/50" : "border-zinc-700"}`}>
                  {done ? <Check size={14} /> : <Icon size={14} />}
                </div>
                <span className="text-[12px] font-bold uppercase tracking-wider truncate w-full text-center">{s.title}</span>
              </button>
            );
          })}
        </div>

        {/* Step 1: HWK */}
        {step === 1 && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4" data-testid="onboarding-section-hwk">
            <div className="flex items-center gap-2"><ClipboardCheck className="text-[#00E575]" size={18} /><h2 className="font-display text-lg font-semibold">Handwerkskammer-Eintragung</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-1">
                <label className={labelCls}>HWK-Nr. *</label>
                <input type="text" value={form.hwk_number} onChange={(e) => setForm({...form, hwk_number: e.target.value})} data-testid="onboarding-hwk-number" placeholder="z. B. A-12345" className={inputCls} />
              </div>
              <div className="sm:col-span-1">
                <label className={labelCls}>HWK-Behörde *</label>
                <input type="text" value={form.hwk_authority} onChange={(e) => setForm({...form, hwk_authority: e.target.value})} data-testid="onboarding-hwk-authority" placeholder="z. B. HWK Region Stuttgart" className={inputCls} />
              </div>
            </div>
            <p className="text-[12px] text-zinc-300 font-medium">Wir prüfen die Eintragung im HWK-Verzeichnis. Bitte gib die Nummer 1:1 wie auf deiner Eintragungsurkunde an.</p>
            <div className="flex justify-end">
              <button onClick={async () => { if (await savePartial({ hwk_number: form.hwk_number, hwk_authority: form.hwk_authority })) setStep(2); }} disabled={saving || !form.hwk_number || !form.hwk_authority} data-testid="onboarding-next-1" className="h-10 px-5 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                Weiter <ArrowRight size={14} />
              </button>
            </div>
          </section>
        )}

        {/* Step 2: Insurance */}
        {step === 2 && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4" data-testid="onboarding-section-insurance">
            <div className="flex items-center gap-2"><ShieldCheck className="text-[#00E575]" size={18} /><h2 className="font-display text-lg font-semibold">Versicherungsnachweis</h2></div>
            <p className="text-[12px] text-zinc-400 leading-relaxed font-medium">Lade deinen gültigen Versicherungsnachweis als PDF, JPG, PNG oder HEIC hoch (Betriebshaftpflicht, max. 8 MB).</p>

            {profile?.insurance_doc_path ? (
              <div className="bg-[#00E575]/10 border border-[#00E575]/30 rounded-lg p-3 flex items-center gap-2 text-[12.5px]" data-testid="onboarding-insurance-status">
                <FileCheck className="text-[#00E575]" size={16} />
                <div className="flex-1 truncate">
                  <div className="font-bold text-[#00E575]">Hochgeladen</div>
                  <div className="text-zinc-300 text-[12px] truncate">{profile.insurance_doc_filename} ({Math.round((profile.insurance_doc_size || 0) / 1024)} KB)</div>
                </div>
              </div>
            ) : (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-2 text-[12.5px] text-amber-200">
                <AlertCircle size={16} /> Noch keine Datei hochgeladen.
              </div>
            )}

            <div>
              <label className={`${inputCls} h-auto py-3 cursor-pointer border-dashed border-zinc-600 hover:border-[#00E575] flex items-center justify-center gap-2 text-zinc-300`}>
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? "lädt hoch…" : (profile?.insurance_doc_path ? "Andere Datei wählen" : "Datei auswählen")}
                <input type="file" accept=".pdf,application/pdf,image/jpeg,image/png,image/heic" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="onboarding-insurance-upload" />
              </label>
            </div>

            <div>
              <label className={labelCls}>Versicherung gültig bis (optional)</label>
              <input type="date" value={form.insurance_expires_at} onChange={(e) => setForm({...form, insurance_expires_at: e.target.value})} data-testid="onboarding-insurance-expires" className={inputCls} />
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="h-10 px-4 rounded-lg bg-zinc-800 text-[13px] hover:bg-zinc-700">Zurück</button>
              <button onClick={async () => { if (await savePartial({ insurance_expires_at: form.insurance_expires_at })) setStep(3); }} disabled={saving || !profile?.insurance_doc_path} data-testid="onboarding-next-2" className="h-10 px-5 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                Weiter <ArrowRight size={14} />
              </button>
            </div>
          </section>
        )}

        {/* Step 3: Service area & rate */}
        {step === 3 && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4" data-testid="onboarding-section-area">
            <div className="flex items-center gap-2"><MapPin className="text-[#00E575]" size={18} /><h2 className="font-display text-lg font-semibold">Servicegebiet & Konditionen</h2></div>
            <div>
              <label className={labelCls}>PLZ-Bereiche (Komma-getrennt, 2–5 stellig)</label>
              <input type="text" value={form.plz_prefixes} onChange={(e) => setForm({...form, plz_prefixes: e.target.value})} data-testid="onboarding-plz" placeholder="z. B. 70, 71, 72, 7340" className={inputCls} />
              <p className="text-[10.5px] text-zinc-500 mt-1">Bestimmt nur die Anzeige — alle Auktionen sind für dich sichtbar.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Max. Anfahrt (km)</label>
                <input type="number" value={form.radius_km} onChange={(e) => setForm({...form, radius_km: e.target.value})} placeholder="50" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Stundensatz brutto (€)</label>
                <input type="number" step="0.01" value={form.hourly_rate} onChange={(e) => setForm({...form, hourly_rate: e.target.value})} data-testid="onboarding-rate" placeholder="85,00" className={inputCls} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Referenzen / Besondere Qualifikationen (optional)</label>
              <textarea value={form.references_text} onChange={(e) => setForm({...form, references_text: e.target.value})} rows={4} placeholder="z. B. WP-Zertifizierung, Hersteller-Schulungen, abgeschlossene Projekte…" className={`${inputCls} h-auto py-2`} />
            </div>
            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="h-10 px-4 rounded-lg bg-zinc-800 text-[13px] hover:bg-zinc-700">Zurück</button>
              <button onClick={async () => {
                const plz = form.plz_prefixes.split(",").map((s) => s.trim()).filter(Boolean);
                if (await savePartial({
                  service_area: { plz_prefixes: plz, radius_km: form.radius_km ? Number(form.radius_km) : null, free_text: null },
                  hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
                  references_text: form.references_text,
                })) setStep(4);
              }} disabled={saving} data-testid="onboarding-next-3" className="h-10 px-5 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                Weiter <ArrowRight size={14} />
              </button>
            </div>
          </section>
        )}

        {/* Step 4: Summary & Submit */}
        {step === 4 && (
          <section className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-4" data-testid="onboarding-section-submit">
            <div className="flex items-center gap-2"><FileCheck className="text-[#00E575]" size={18} /><h2 className="font-display text-lg font-semibold">Übersicht & Absenden</h2></div>
            <dl className="text-[13px] divide-y divide-zinc-800">
              <Row label="HWK-Nr." value={profile?.hwk_number} />
              <Row label="HWK-Behörde" value={profile?.hwk_authority} />
              <Row label="Versicherungsnachweis" value={profile?.insurance_doc_filename ? `✓ ${profile.insurance_doc_filename}` : "—"} />
              <Row label="PLZ-Bereiche" value={(profile?.service_area?.plz_prefixes || []).join(", ") || "—"} />
              <Row label="Stundensatz" value={profile?.hourly_rate ? `${profile.hourly_rate} € brutto` : "—"} />
            </dl>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-[12px] text-amber-200 flex items-start gap-2 font-medium">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <p>Nach dem Absenden kannst du dein Profil bis zur Freigabe nicht mehr ändern. Bei Rückfragen meldet sich das Novatus-Team per E-Mail.</p>
            </div>
            <div className="flex justify-between">
              <button onClick={() => setStep(3)} className="h-10 px-4 rounded-lg bg-zinc-800 text-[13px] hover:bg-zinc-700">Zurück</button>
              <button onClick={submit} disabled={submitting} data-testid="onboarding-submit" className="h-10 px-5 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-50">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Zur Prüfung absenden
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="py-2 flex justify-between gap-4">
      <dt className="text-zinc-400">{label}</dt>
      <dd className="text-zinc-100 font-medium text-right truncate">{value || "—"}</dd>
    </div>
  );
}

function SubmittedView({ profile }) {
  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="max-w-md bg-[#18181B] border border-[#00E575]/30 rounded-2xl p-6 text-center">
        <FileCheck className="text-[#00E575] mx-auto mb-3" size={40} />
        <h1 className="font-display text-2xl font-bold mb-2">Bewerbung eingereicht</h1>
        <p className="text-[13px] text-zinc-300 mb-4">
          Vielen Dank! Wir prüfen deine Angaben — in der Regel innerhalb von <b>1–2 Werktagen</b>.
          Du bekommst eine E-Mail, sobald wir entschieden haben.
        </p>
        <p className="text-[12px] text-zinc-300 font-medium">Eingereicht: {profile?.submitted_at ? new Date(profile.submitted_at).toLocaleString("de-DE") : "—"}</p>
      </div>
    </div>
  );
}

function ApprovedView({ onContinue }) {
  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="max-w-md bg-[#18181B] border border-[#00E575]/40 rounded-2xl p-6 text-center">
        <Check className="text-[#00E575] mx-auto mb-3 bg-[#00E575]/20 rounded-full p-2" size={48} />
        <h1 className="font-display text-2xl font-bold mb-2">Bewerbung angenommen!</h1>
        <p className="text-[13px] text-zinc-300 mb-5">Willkommen im Novatus-Installateur-Netzwerk. Du kannst jetzt auf Montageaufträge bieten.</p>
        <button onClick={onContinue} className="h-11 px-6 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[14px] hover:bg-[#00C964]">
          Zum Dashboard
        </button>
      </div>
    </div>
  );
}

function RejectedView({ profile }) {
  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="max-w-md bg-[#18181B] border border-red-500/40 rounded-2xl p-6">
        <AlertCircle className="text-red-400 mb-3" size={40} />
        <h1 className="font-display text-2xl font-bold mb-2">Bewerbung abgelehnt</h1>
        <p className="text-[13px] text-zinc-300 mb-3">Leider können wir deinen Account aktuell nicht freischalten.</p>
        {profile?.rejected_reason && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-3">
            <div className="text-[12px] uppercase tracking-widest text-red-300 font-bold mb-1">Begründung</div>
            <div className="text-[13px] text-zinc-100">{profile.rejected_reason}</div>
          </div>
        )}
        <p className="text-[12px] text-zinc-300 font-medium">Bei Fragen erreichst du uns unter <a href="mailto:installer@novatussales.pro" className="text-[#00E575]">installer@novatussales.pro</a>.</p>
      </div>
    </div>
  );
}
